import "zod/compile";
import { z } from "zod";

/**
 * The organization-level Ship & Debit 849 settings an S&D admin can see and
 * change from the app (SD-86), and which of the TWO stores each one lives in.
 *
 * Two stores, because that is where the readers already look:
 * - `entity`: the `entity_module_config` row with `entityType = "organization"`
 *   and module `ship-and-debit` (`ShipAndDebitEntityConfig`).
 * - `org`: `org_module_config.config.shipAndDebit` (`ModuleConfig["shipAndDebit"]`).
 *
 * Setting `sd_849_judgment_enabled` in the wrong store once succeeded, read
 * back fine from that same wrong store, and left the feature off — so the store
 * is declared here, once, rather than decided by whoever writes the setting.
 *
 * Labels and descriptions are user-facing text and live in the frontend (i18n);
 * this file holds only what the backend must also agree on.
 */
export type Sd849OrgSettingStore = "entity" | "org";

/** Credit rounding tolerance ceiling: an accepted line is terminal, so this silently writes off the shortfall. */
export const SD_849_CREDIT_ROUNDING_TOLERANCE_MAX = 0.05;
/** 849 Review queue cap bounds; `resolveReviewQueueCap` clamps to the same. */
export const SD_849_REVIEW_QUEUE_MIN_CAP = 1;
export const SD_849_REVIEW_QUEUE_MAX_CAP = 200;
export const SD_849_REVIEW_QUEUE_DEFAULT_CAP = 50;
/** Audit sample ceiling; `resolveAuditSampleRate` clamps to the same. */
export const SD_849_AUDIT_SAMPLE_MAX_RATE = 0.2;

interface BooleanSetting {
  kind: "boolean";
  store: Sd849OrgSettingStore;
}
interface NumberSetting {
  kind: "number";
  store: Sd849OrgSettingStore;
  min: number;
  max: number;
  /** Whole numbers only. */
  integer?: boolean;
  /** What the reader uses when the key is unset — shown as "not set (defaults to …)". */
  unsetMeans: number;
}
interface TextSetting {
  kind: "text";
  store: Sd849OrgSettingStore;
}
export type Sd849OrgSettingSpec = BooleanSetting | NumberSetting | TextSetting;

/**
 * Every org-level 849 setting. An unset boolean means OFF to every reader.
 *
 * Not here on purpose: `sd_849_group_by_claim` (retired by #7657, read by
 * nothing) and `sd_849_email_matching_enabled` (read per supplier COMPANY, so it
 * is a supplier setting, edited on the supplier page).
 */
export const SD_849_ORG_SETTINGS = {
  sd_849_structural_signals_enabled: { kind: "boolean", store: "entity" },
  sd_849_judgment_enabled: { kind: "boolean", store: "entity" },
  sd_849_credit_rounding_tolerance: {
    kind: "number",
    store: "entity",
    min: 0,
    max: SD_849_CREDIT_ROUNDING_TOLERANCE_MAX,
    unsetMeans: 0,
  },
  sd_849_review_queue_cap: {
    kind: "number",
    store: "org",
    min: SD_849_REVIEW_QUEUE_MIN_CAP,
    max: SD_849_REVIEW_QUEUE_MAX_CAP,
    integer: true,
    unsetMeans: SD_849_REVIEW_QUEUE_DEFAULT_CAP,
  },
  sd_849_audit_sample_rate: {
    kind: "number",
    store: "org",
    min: 0,
    max: SD_849_AUDIT_SAMPLE_MAX_RATE,
    unsetMeans: 0,
  },
  sd_849_email_context: { kind: "text", store: "entity" },
} as const satisfies Record<string, Sd849OrgSettingSpec>;

export type Sd849OrgSettingKey = keyof typeof SD_849_ORG_SETTINGS;

export const SD_849_ORG_SETTING_KEYS = Object.keys(
  SD_849_ORG_SETTINGS,
) as Sd849OrgSettingKey[];

/** The current value of every setting; an absent key is UNSET in its store. */
export const Sd849OrgSettingsValuesSchema = z.object({
  sd_849_structural_signals_enabled: z.boolean().optional(),
  sd_849_judgment_enabled: z.boolean().optional(),
  sd_849_credit_rounding_tolerance: z.number().optional(),
  sd_849_review_queue_cap: z.number().optional(),
  sd_849_audit_sample_rate: z.number().optional(),
  sd_849_email_context: z.string().optional(),
});
export type Sd849OrgSettingsValues = z.infer<
  typeof Sd849OrgSettingsValuesSchema
>;

/**
 * A change to some of the settings. ONLY the keys being changed are sent; an
 * omitted key is left exactly as stored. `null` unsets a key. Strict, so a
 * misspelt key is refused instead of silently doing nothing. Values are checked
 * against the bounds above — the same bounds the readers clamp to.
 */
export const Sd849OrgSettingsPatchSchema = z.strictObject({
  sd_849_structural_signals_enabled: z.boolean().nullable().optional(),
  sd_849_judgment_enabled: z.boolean().nullable().optional(),
  sd_849_credit_rounding_tolerance: z
    .number()
    .min(0)
    .max(SD_849_CREDIT_ROUNDING_TOLERANCE_MAX)
    .nullable()
    .optional(),
  sd_849_review_queue_cap: z
    .number()
    .int()
    .min(SD_849_REVIEW_QUEUE_MIN_CAP)
    .max(SD_849_REVIEW_QUEUE_MAX_CAP)
    .nullable()
    .optional(),
  sd_849_audit_sample_rate: z
    .number()
    .min(0)
    .max(SD_849_AUDIT_SAMPLE_MAX_RATE)
    .nullable()
    .optional(),
  sd_849_email_context: z.string().nullable().optional(),
});
export type Sd849OrgSettingsPatch = z.infer<typeof Sd849OrgSettingsPatchSchema>;

/**
 * The patch that turns `saved` into `draft`: only keys whose value differs.
 * A key present in `saved` and absent (undefined) in `draft` becomes `null`
 * (unset). Keys equal in both are never sent, so saving one setting can never
 * rewrite another from a stale copy.
 */
export function diffSd849OrgSettings(
  saved: Sd849OrgSettingsValues,
  draft: Sd849OrgSettingsValues,
): Sd849OrgSettingsPatch {
  const patch: Record<string, unknown> = {};
  for (const key of SD_849_ORG_SETTING_KEYS) {
    const before = saved[key];
    const after = draft[key];
    if (before === after) continue;
    patch[key] = after === undefined ? null : after;
  }
  return patch as Sd849OrgSettingsPatch;
}
